import dayjs from 'dayjs';
import DatePicker, { registerLocale } from 'react-datepicker';
import Parser from 'html-react-parser';
import { vi } from 'date-fns/locale/vi';

registerLocale('vi', vi);

import Card from '../../../components/Card';
import Loading from '../../../components/Loading';
import { formatDisplayNumber } from '../../../utils/format';
import { getStatusColor } from '../../../utils/helpers';
import useWindowDimensions from '../../../hooks/useWindowDimensions';

import { getSeriesDataForSectorChart } from '../Dashboard.helpers';
import * as Styles from '../Dashboard.styles';

export default function SectorTrending({
  isLoading,
  tradingVolumeData,
  upTrendingData,
  isUpTrendingDataLoading,
  onMonthChange,
  sectorTrendingDate,
}) {
  const { width } = useWindowDimensions();
  const { sectorInfo } = getSeriesDataForSectorChart(
    tradingVolumeData,
    '',
    'klgd',
    width,
    'cp',
    1,
    'right'
  );

  const getSectorInfo = (sectorData) => {
    const totalValue = sectorData.sectors.reduce(
      (total, item) => total + Number(item.gtgd),
      0
    );
    const totalVolume = sectorData.sectors.reduce(
      (total, item) => total + Number(item.klgd),
      0
    );
    const currentSector = sectorData.sectors.find(
      (s) => s.id === sectorData.sectorIdUpTrend
    );
    return {
      companies: currentSector?.companies,
      valuePercent: ((currentSector?.gtgd * 100) / totalValue).toFixed(2),
      value: currentSector?.gtgd,
      volumePercent: ((currentSector?.klgd * 100) / totalVolume).toFixed(2),
      volume: currentSector?.klgd,
    };
  };

  const getTrendingData = (date, _upTrendingData) => {
    const currentDate = dayjs(date).format('YYYYMMDD');
    const trending = _upTrendingData.find((t) => t.ngay === currentDate);
    if (!trending) {
      return null;
    }
    const item = getSectorInfo(trending);
    let size = ((item.volumePercent * 40) / 50) * 2.3;
    if (size < 24) {
      size = 24;
    }
    const selectedItem = sectorInfo.find(
      (s) => Number(s.id) === Number(trending.sectorIdUpTrend)
    );
    return {
      size,
      ...selectedItem,
      ...item,
    };
  };

  const renderTrendingDot = ({ size, color }) => {
    if (!size) {
      return null;
    }
    return (
      <span
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 block rounded-full"
        style={{ width: `${size}px`, height: `${size}px`, background: color }}
      >
        &nbsp;
      </span>
    );
  };

  const getTooltip = (item, date) => {
    let companiesHtml = '';
    for (const company of item.companies) {
      const color = getStatusColor(
        company.giadongcua,
        company.giathamchieu,
        company.giatran,
        company.giasan
      );
      companiesHtml += `<div class="flex items-center mt-1">
        <span class="w-3.5 h-3.5 min-w-3.5 rounded-full inline-block mr-2" style="background: ${color}"></span>
        <span class="mr-1">${company.machungkhoan}: </span>
        <span class="font-bold">${formatDisplayNumber(
          company.thaydoi
        )} (${company.thaydoiphantram.toFixed(2)}%)</span>
      </div>`;
    }
    return `<div class="text-gray-600 text-sm p-2 inline-block">
        <div class="mb-1 text-medium">${dayjs(date).format('DD/MM/YYYY')}</div>
        <div>-----------------------</div>
        <div class="mb-1 text-medium font-bold"><span class="w-3.5 h-3.5 min-w-3.5 rounded-full inline-block mr-2" style="background: ${
          item.color
        };"></span> ${item.name}</div>
        <div class="flex justify-between pr-3 py-1">
          <div class="mr-2">KLGD:</div>
          <div class="text-right font-bold">${formatDisplayNumber(
            item.volume
          )} cp(${formatDisplayNumber(item.volumePercent)}%)</div>
        </div>
        <div class="flex justify-between pr-3 py-1">
          <div class="mr-2">GTGD:</div>
          <div class="text-right font-bold">${formatDisplayNumber(
            (item.value / 1000).toFixed(2)
          )} tỷ(${formatDisplayNumber(item.valuePercent)}%)</div>
        </div>
        <div>-----------------------</div>
        <div class="mb-1 text-medium font-bold">Top 5 tăng %</div>
        <div>
          ${companiesHtml}
        </div>
      </div>`;
  };

  const renderDayContents = (day, date) => {
    const data = getTrendingData(date, upTrendingData);
    if (!data?.size) {
      return (
        <span className="relative w-12 h-12 inline-flex items-center justify-center">
          <span className={`relative z-20 text-gray-600 dark:text-gray-300`}>
            {day}
          </span>
        </span>
      );
    }

    return (
      <Styles.Tooltip
        title={Parser(getTooltip(data, date))}
        placement="top"
        arrow
        className="sector-trending"
      >
        <span className="relative w-12 h-12 inline-flex items-center justify-center cursor-pointer">
          {renderTrendingDot({ size: data?.size, color: data?.color })}
          <span
            className={`relative z-20 ${
              data.size ? 'text-white' : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {day}
          </span>
        </span>
      </Styles.Tooltip>
    );
  };

  const handleMonthChange = (date) => {
    if (onMonthChange) {
      onMonthChange(dayjs(date).startOf('month'), dayjs(date).endOf('month'));
    }
  };

  const renderContent = () => {
    return (
      <DatePicker
        inline
        renderDayContents={renderDayContents}
        locale="vi"
        onMonthChange={handleMonthChange}
        selected={new Date(sectorTrendingDate.startDate)}
        calendarStartDay={0}
      />
    );
  };

  return (
    <Card title="Xu Hướng Theo Ngành" contentClassName="mt-0">
      {isLoading || isUpTrendingDataLoading ? <Styles.EmptyTag /> : null}
      <Loading
        isLoading={isLoading || isUpTrendingDataLoading}
        className="white"
      />
      {!isLoading && !isUpTrendingDataLoading ? (
        <div className="flex justify-between flex-col">
          <div className="w-full sector-calendar-content">
            {renderContent()}
          </div>
        </div>
      ) : null}
    </Card>
  );
}
